import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Shield, ShieldOff, User } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import UserSearch from './UserSearch';

interface AdminUserSummary {
  user_id: string;
  email: string;
  name?: string;
  is_admin: boolean;
  auth_provider?: string;
  is_federated?: boolean;
}

interface AdminUserListResponse {
  items: AdminUserSummary[];
  total: number;
}

interface AdminRoleMutationResponse {
  user: AdminUserSummary;
  message: string;
}

interface UserSearchInfo {
  user_id: string;
  email: string;
  email_verified: boolean;
  name?: string;
  phone_number?: string;
  created_at?: string;
  updated_at?: string;
}

const AdminRoleManagementSection: React.FC = () => {
  const authenticatedApi = useAuthenticatedApi();
  const { user: currentUser } = useAuth();

  const [adminUsers, setAdminUsers] = useState<AdminUserSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserSearchInfo | null>(null);
  const [userSearchClearSignal, setUserSearchClearSignal] = useState(0);

  const currentAdminUserId = currentUser?.id || '';
  const totalAdmins = adminUsers.length;

  const loadAdminUsers = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await authenticatedApi.get<AdminUserListResponse>(apiEndpoints.admin.listAdmins);
      setAdminUsers(response.items || []);
    } catch (error) {
      console.error('Failed to load admin users:', error);
      setErrorMessage('Failed to load admin users');
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedApi]);

  useEffect(() => {
    void loadAdminUsers();
  }, [loadAdminUsers]);

  const selectedUserAlreadyAdmin = useMemo(() => {
    if (!selectedUser) {
      return false;
    }
    return adminUsers.some((adminUser) => adminUser.user_id === selectedUser.user_id);
  }, [selectedUser, adminUsers]);

  const clearSelectedUser = useCallback(() => {
    setSelectedUser(null);
    setUserSearchClearSignal((previousSignal) => previousSignal + 1);
  }, []);

  const handleGrantAdmin = useCallback(async () => {
    if (!selectedUser) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const response = await authenticatedApi.post<AdminRoleMutationResponse>(
        apiEndpoints.admin.grantAdmin,
        { identifier: selectedUser.user_id },
      );
      setSuccessMessage(response.message);
      clearSelectedUser();
      await loadAdminUsers();
    } catch (error) {
      console.error('Failed to grant admin role:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to grant admin role');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedUser, authenticatedApi, loadAdminUsers, clearSelectedUser]);

  const handleRevokeAdmin = useCallback(
    async (adminUser: AdminUserSummary) => {
      if (!window.confirm(`Revoke admin role from ${adminUser.email}?`)) {
        return;
      }

      setIsSubmitting(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      try {
        const response = await authenticatedApi.post<AdminRoleMutationResponse>(
          apiEndpoints.admin.revokeAdmin,
          { identifier: adminUser.user_id },
        );
        setSuccessMessage(response.message);
        await loadAdminUsers();
      } catch (error) {
        console.error('Failed to revoke admin role:', error);
        setErrorMessage(error instanceof Error ? error.message : 'Failed to revoke admin role');
      } finally {
        setIsSubmitting(false);
      }
    },
    [authenticatedApi, loadAdminUsers],
  );

  return (
    <div className='p-6'>
      <h2 className='text-2xl font-semibold text-ui-text-primary mb-2'>Admin Role Management</h2>
      <p className='text-ui-text-secondary mb-6'>
        Manage platform admins from the database source of truth.
      </p>

      {errorMessage && (
        <div className='mb-4 flex items-center rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'>
          <AlertCircle size={16} className='mr-2 flex-shrink-0' />
          <span className='text-sm'>{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className='mb-4 flex items-center rounded-lg border border-green-200 bg-green-50 p-3 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'>
          <CheckCircle size={16} className='mr-2 flex-shrink-0' />
          <span className='text-sm'>{successMessage}</span>
        </div>
      )}

      <div className='mb-8 rounded-lg border border-ui-border-primary bg-ui-background-tertiary p-4 dark:border-ui-border-primary dark:bg-ui-background-secondary'>
        <h3 className='mb-3 text-lg font-semibold text-ui-text-primary'>Grant Admin Role</h3>
        <UserSearch
          onUserSelect={setSelectedUser}
          disabled={isSubmitting}
          label='User ID or Email'
          placeholder='Find a user to promote'
          buttonText='Select User'
          clearAfterSelect={false}
          clearSignal={userSearchClearSignal}
        />
        {selectedUser && (
          <div className='mt-4 space-y-3'>
            <div className='rounded-lg border border-ui-border-primary bg-white p-3 text-sm dark:border-ui-border-primary dark:bg-ui-background-tertiary'>
              <p className='font-medium text-ui-text-primary'>
                Selected user: {selectedUser.name || selectedUser.email}
              </p>
              <p className='text-ui-text-secondary'>{selectedUser.email}</p>
              <p className='font-mono text-xs text-ui-text-tertiary'>{selectedUser.user_id}</p>
            </div>

            <div className='flex flex-wrap items-center gap-3'>
              <button
                onClick={handleGrantAdmin}
                disabled={isSubmitting || selectedUserAlreadyAdmin}
                className='inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-neutral-500'
              >
                <Shield size={16} className='mr-2' />
                Grant Admin
              </button>
              <button
                onClick={clearSelectedUser}
                disabled={isSubmitting}
                className='rounded-lg border border-ui-border-primary px-4 py-2 text-sm font-medium text-ui-text-secondary hover:bg-ui-background-tertiary disabled:cursor-not-allowed disabled:opacity-50 dark:border-ui-border-primary dark:text-ui-text-tertiary dark:hover:bg-ui-background-tertiary'
              >
                Clear Selection
              </button>
              {selectedUserAlreadyAdmin && (
                <span className='text-sm text-ui-text-secondary'>
                  {selectedUser.email} is already an admin.
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className='rounded-lg border border-ui-border-primary bg-white p-4 dark:border-ui-border-primary dark:bg-ui-background-secondary'>
        <div className='mb-4 flex items-center justify-between'>
          <h3 className='text-lg font-semibold text-ui-text-primary'>Current Admins</h3>
          <span className='text-sm text-ui-text-secondary'>{totalAdmins} total</span>
        </div>

        {isLoading ? (
          <p className='text-sm text-ui-text-secondary'>Loading admins...</p>
        ) : adminUsers.length === 0 ? (
          <p className='text-sm text-ui-text-secondary'>No admin users found.</p>
        ) : (
          <div className='space-y-3'>
            {adminUsers.map((adminUser) => {
              const isCurrentUser = adminUser.user_id === currentAdminUserId;
              const disableRevoke = isSubmitting || isCurrentUser || totalAdmins <= 1;
              return (
                <div
                  key={adminUser.user_id}
                  className='flex flex-col gap-3 rounded-lg border border-ui-border-primary p-3 dark:border-ui-border-primary md:flex-row md:items-center md:justify-between'
                >
                  <div className='min-w-0'>
                    <div className='flex items-center gap-2'>
                      <User size={16} className='text-ui-text-tertiary' />
                      <span className='truncate text-sm font-medium text-ui-text-primary'>
                        {adminUser.name || adminUser.email}
                      </span>
                      {isCurrentUser && (
                        <span className='rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'>
                          You
                        </span>
                      )}
                    </div>
                    <p className='truncate text-xs text-ui-text-secondary'>{adminUser.email}</p>
                    <p className='text-xs text-ui-text-tertiary'>
                      Provider: {adminUser.auth_provider || 'unknown'}
                    </p>
                  </div>

                  <button
                    onClick={() => handleRevokeAdmin(adminUser)}
                    disabled={disableRevoke}
                    className='inline-flex items-center self-start rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-neutral-500 md:self-auto'
                  >
                    <ShieldOff size={16} className='mr-2' />
                    Revoke Admin
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRoleManagementSection;
